import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ViewState, InspectionRecord, CrackAnalysis, Severity, Project, InspectionMode, Finding, TelemetryPoint, ProjectMetadata } from './types';
import { LiveScanner } from './components/LiveScanner';
import { VideoInspectorT } from './components/VideoInspectorT';
import { VideoInspectorS } from './components/VideoInspectorS';
import { VideoInspectorM } from './components/VideoInspectorM';
import { ImageInspector } from './components/ImageInspector';
import { AnalysisResults } from './components/AnalysisResults';
import { TrainingModule } from './components/TrainingModule';
import { ProjectModal } from './components/ProjectModal';
import { saveFileToProject, saveSessionCheckpoint, getProjectHandle, setProjectHandle, discoverProjectAssets, readProjectRecords, readEvidenceRecords, getSessionCheckpoint, loadProjectFromLocal } from './services/fileSystemService';
import { getVaultEntries } from './services/vaultService';
import { parseTrajectoryCsv, detectMissionStart, calculateAssetGeometry, parseLasFile, parseTextPointCloud, filterPointsToAssetBoundary, detectTrajectoryOrientation } from './services/telemetryService';
import { TrajectoryMapT } from './components/TrajectoryMapT';
import { TrajectoryMapA } from './components/TrajectoryMapA';
import { MissionPlanner } from './components/MissionPlanner';
import { LoginGuard } from './components/LoginGuard';
import { AdminLogButton } from './components/AdminLogButton';
import { analyzeStaticSurface } from './services/claudeService';
import { generateUserManual, generateRegulationsPdf, generateGDPRManual } from './services/manualService';
import { logSessionEnd } from './services/logService';
import { LoadingOverlay } from './components/LoadingOverlay';
import { exportFindingsToZip } from './services/zipService';

const INSPECT_TOOLS = [
  { id: 'none', label: 'Select Camera', specs: 'Hardware profile not selected. Perspective calibration is disabled.', defaultFps: 30 },
  { id: 'Elios3 UAV Chambers', label: 'Elios3 with Telemetry', specs: '4K UHD camera (3840x2160 @ 30fps) with a 1/2.3” CMOS sensor and a 2.71 mm fixed-focal lens, providing a 114° horizontal and 131° diagonal FOV.', defaultFps: 30 },
  { id: 'Elios3 Video Only', label: 'Video without Telemetry', specs: 'Standard inspection configuration without spatial telemetry mapping requirements.', defaultFps: 30 },
  { id: 'custom', label: 'Read Camera exif or Meta data', specs: 'User-defined parameters for custom sensor rigs.', defaultFps: 30 }
];

const PROTOCOL_OPTIONS = [
  {
    id: InspectionMode.CRACK_ONLY,
    label: 'Crack Detection',
    icon: <svg className="w-14 h-14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    desc: 'Surface fissures & deep scratches.'
  },
  {
    id: InspectionMode.WELD_INTEGRITY,
    label: 'Weld Integrity',
    icon: <svg className="w-14 h-14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    desc: 'Junctions & HAZ audit.'
  },
  {
    id: InspectionMode.GENERAL_NDT,
    label: 'Structural Audit',
    icon: <svg className="w-14 h-14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    desc: 'Corrosion, dents & deformation.'
  },
  {
    id: InspectionMode.FOULING_INSPECTION,
    label: 'Cleanliness',
    icon: <svg className="w-14 h-14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.691.31a2 2 0 01-1.782 0l-.691-.31a6 6 0 00-3.86-.517l-2.388.477a2 2 0 00-1.022.547l-.34.34a2 2 0 000 2.829l1.245 1.244a2 2 0 002.829 0l.144-.144a2 2 0 000-2.829l-1.245-1.244" /></svg>,
    desc: 'Residue & biological scaling.'
  }
];

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('HOME');
  const [history, setHistory] = useState<InspectionRecord[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeModes, setActiveModes] = useState<InspectionMode[]>([InspectionMode.CRACK_ONLY]);

  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [currentAnalysis, setCurrentAnalysis] = useState<CrackAnalysis | null>(null);
  const [videoTimeline, setVideoTimeline] = useState<CrackAnalysis[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [telemetryData, setTelemetryData] = useState<TelemetryPoint[]>([]);
  const [modelPoints, setModelPoints] = useState<TelemetryPoint[]>([]);
  const [missionStart, setMissionStart] = useState<number | undefined>(undefined);

  // Staging for Image Inspector
  const [pendingImages, setPendingImages] = useState<File[]>([]);

  const [lastVideoFile, setLastVideoFile] = useState<File | null>(null);
  const [lastVideoRange, setLastVideoRange] = useState<[number, number] | null>(null);
  const [lastTrajectoryPoints, setLastTrajectoryPoints] = useState<TelemetryPoint[] | null>(null);
  const [lastLasModelName, setLastLasModelName] = useState<string | null>(null);
  const [lastModelPoints, setLastModelPoints] = useState<TelemetryPoint[]>([]);
  const [lastOverlap, setLastOverlap] = useState<number | undefined>(undefined);
  const [lastGeometry, setLastGeometry] = useState<any>(null);
  const [lastTimeline, setLastTimeline] = useState<CrackAnalysis[]>([]);
  const [lastInspectorView, setLastInspectorView] = useState<ViewState | null>(null);

  // Tool ID state now managed by the buttons
  const [selectedToolId, setSelectedToolId] = useState<string>('none');
  const selectedTool = INSPECT_TOOLS.find(t => t.id === selectedToolId) || INSPECT_TOOLS[0];
  const [labTransferData, setLabTransferData] = useState<{ image: string, findings: Finding[], timeline: CrackAnalysis[], allFindings: Finding[], hasTelemetry?: boolean, customer?: string, asset?: string } | null>(null);
  const [labUpdatedFindings, setLabUpdatedFindings] = useState<Finding[] | null>(null);
  const [persistentCloudOffset, setPersistentCloudOffset] = useState({ x: 0, y: 0 });
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Consolidate findings across the timeline for mapping
  const consolidatedFindings = useMemo(() => {
    if (!videoTimeline || videoTimeline.length === 0) return currentAnalysis?.findings || [];
    const seen = new Set<string>();
    const aggregated: (Finding & { timestamp?: number, frameImage?: string, originalAnalysis?: CrackAnalysis })[] = [];
    videoTimeline.forEach(t => {
      (t.findings || []).forEach(f => {
        const key = `${f.type}-${f.box.join(',')}`;
        if (!seen.has(key)) {
          seen.add(key);
          aggregated.push({
            ...f,
            timestamp: f.timestamp ?? t.timestamp,
            frameImage: t.frameImage,
            originalAnalysis: t
          });
        }
      });
    });
    return aggregated;
  }, [videoTimeline, currentAnalysis]);

  // Load initial state + compact any bloated old history entry
  useEffect(() => {
    try {
      const savedProjects = localStorage.getItem('staincheck_projects');
      if (savedProjects) setProjects(JSON.parse(savedProjects));

      const savedHistory = localStorage.getItem('staincheck_history');
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        setHistory(parsed);

        // Compact: strip base64 images from the stored entry immediately.
        // This repairs old bloated entries that caused QuotaExceededError.
        const compacted = parsed.slice(0, 20).map((record: any) => ({
          ...record,
          imageUrl: '',
          analysis: record.analysis ? {
            ...record.analysis,
            frameImage: undefined,
            findings: record.analysis.findings?.map((f: any) => ({ ...f, frameImage: undefined }))
          } : record.analysis,
          timeline: record.timeline?.map((t: any) => ({ ...t, frameImage: undefined }))
        }));
        try {
          localStorage.setItem('staincheck_history', JSON.stringify(compacted));
        } catch (_) {
          // Still too large — nuke it entirely and let it rebuild from scratch
          console.warn('[Init] staincheck_history still too large after compaction, clearing.');
          localStorage.removeItem('staincheck_history');
        }
      }
    } catch (e) {
      console.error("Failed to load historical data from disk:", e);
      // If corrupted / oversized, clear it so future saves work
      localStorage.removeItem('staincheck_history');
    }
  }, []);

  // Update Mission Start when telemetry changes
  useEffect(() => {
    if (telemetryData.length > 0) {
      setMissionStart(detectMissionStart(telemetryData));
    }
  }, [telemetryData]);

  // Sync History to Disk with Data Thinning
  // Strip all base64 image data before writing — imageUrl is the main quota offender
  // (~200KB per record × 15 = 3MB+ which blows the 5MB localStorage limit).
  useEffect(() => {
    try {
      const storageHistory = history.slice(0, 20).map(record => ({
        ...record,
        imageUrl: '',  // strip base64 — regenerated from folder on reload
        analysis: record.analysis ? {
          ...record.analysis,
          frameImage: undefined,
          findings: record.analysis.findings?.map((f: any) => ({ ...f, frameImage: undefined }))
        } : record.analysis,
        timeline: record.timeline?.map(t => ({
          ...t,
          frameImage: undefined
        }))
      }));

      localStorage.setItem('staincheck_history', JSON.stringify(storageHistory));
    } catch (e) {
      console.warn("Storage Quota Exceeded. History will exist in memory for this session but will not persist to disk.", e);
    }
  }, [history]);

  // Sync Projects to Disk
  useEffect(() => {
    try {
      localStorage.setItem('staincheck_projects', JSON.stringify(projects));
    } catch (e) {
      console.error("Failed to save project metadata:", e);
    }
  }, [projects]);

  const saveToHistory = (record: InspectionRecord) => {
    setHistory(prev => [record, ...prev].slice(0, 50));
  };

  const saveProject = (projectNumber: string, metadata?: ProjectMetadata, existingId?: string) => {
    const id = existingId || crypto.randomUUID();
    setProjects(prev => {
      if (prev.find(p => p.id === id)) return prev;
      const newProject: Project = {
        id,
        projectNumber,
        createdAt: Date.now(),
        metadata
      };
      return [newProject, ...prev];
    });
    return id;
  };

  const handleActionWithProject = (action: () => void, isVideo: boolean = false) => {
    setPendingAction(() => action);
    setShowProjectModal(true);
  };

  const onProjectSelected = async (projectId: string, keepHandle: boolean = false, auditMode: boolean = false) => {
    setActiveProjectId(projectId);
    setShowProjectModal(false);
    setIsLoading(true);

    if (!keepHandle) {
      setProjectHandle(null);
    }

    // CLEAR PREVIOUS PROJECT STATE TO PREVENT DATA LEAKAGE
    setVideoTimeline(null);
    setTelemetryData([]);
    setModelPoints([]);
    setCurrentAnalysis(null);
    setCurrentImage(null);
    setLastVideoFile(null);
    setLastVideoRange(null);
    setLastTrajectoryPoints(null);
    setLastLasModelName(null);
    setLastModelPoints([]);
    setLastOverlap(undefined);
    setLastGeometry(null);
    setLastTimeline([]);
    setLabUpdatedFindings(null);
    setLabTransferData(null);
    setPersistentCloudOffset({ x: 0, y: 0 });
    
    // Capture and clear pendingAction so it executes at the end of the load cycle
    const actionToRun = pendingAction;
    setPendingAction(null);

    try {
      const isLocalProject = !!getProjectHandle();
      const physicalAssets = await discoverProjectAssets();

      // ── 1. LOAD VERIFIED FINDINGS (02_Verified_Findings) ────────────────────
      // These are the human-OK'd anomalies. Each JSON is a TrainingExample with
      // { image, timestamp, analysis: { findings: Finding[] } }
      const verifiedRecords = await readProjectRecords();

      // Flatten to individual Finding objects, each carrying its frame image
      const verifiedFindings: any[] = verifiedRecords.flatMap((r: any) => {
        const findings: any[] = r.analysis?.findings || r.findings || [];
        return findings.map((f: any) => ({
          ...f,
          isVerified: true,
          frameImage: r.image || f.frameImage,
          timestamp: r.timestamp,
        }));
      });
      console.log('[AuditLoad] 02_Verified_Findings: records=', verifiedRecords.length, '| individual findings=', verifiedFindings.length);
      // Log first verified finding for key inspection
      if (verifiedFindings.length > 0) {
        const vf = verifiedFindings[0];
        console.log('[AuditLoad] Sample verified finding → type:', vf.type, '| box:', vf.box);
      }

      // ── 2. LOAD RAW EVIDENCE (01_Evidence_Images) ────────────────────────────
      // These are raw AI detections. Each JSON is a CrackAnalysis frame with
      // { findings: Finding[], frameImage, timestamp, ... }
      const evidenceRecords = await readEvidenceRecords();

      // Flatten to individual raw Finding objects with frame image attached
      const rawFindings: any[] = evidenceRecords.flatMap((r: any) => {
        const findings: any[] = r.findings || r.analysis?.findings || [];
        const frameImage = r.frameImage || r.image || null;
        return findings.map((f: any) => ({
          ...f,
          isVerified: false,
          frameImage: f.frameImage || frameImage,
          timestamp: f.timestamp ?? r.timestamp,
        }));
      });
      console.log('[AuditLoad] 01_Evidence_Images: records=', evidenceRecords.length, '| individual findings=', rawFindings.length);
      if (rawFindings.length > 0) {
        const rf = rawFindings[0];
        console.log('[AuditLoad] Sample raw finding → type:', rf.type, '| box:', rf.box);
      }

      // Build a preloaded timeline from verified records for VideoInspector use
      if (verifiedRecords.length > 0) {
        const preloadedTimeline: CrackAnalysis[] = verifiedRecords.flatMap((r: any) => {
          if (r.timeline && Array.isArray(r.timeline) && r.timeline.length > 0) {
            return r.timeline.map((t: CrackAnalysis) => ({
              ...t,
              frameImage: t.frameImage || r.image,
              isVerified: true,
              findings: t.findings || r.analysis?.findings || []
            }));
          }
          return [{
            ...(r.analysis || {}),
            detected: r.analysis?.detected ?? true,
            severity: r.analysis?.severity || 'LOW',
            mode: r.analysis?.mode || 'GENERAL_NDT',
            timestamp: r.timestamp,
            frameImage: r.image,
            isVerified: true,
            findings: r.analysis?.findings || []
          }];
        });
        setLastTimeline(preloadedTimeline);
      } else if (!isLocalProject) {
        // Fallback: ONLY try loading from vault if NOT a local project
        try {
          const vaultEntries = await getVaultEntries();
          if (vaultEntries.length > 0) {
            const vaultTimeline: CrackAnalysis[] = (vaultEntries.flatMap((r: any) => {
              if (r.timeline && Array.isArray(r.timeline) && r.timeline.length > 0) {
                return r.timeline.map((t: any) => ({
                  ...t,
                  frameImage: t.frameImage || r.image,
                  isVerified: true,
                  findings: t.findings || []
                }));
              }
              return [{
                ...(r.analysis || {}),
                detected: r.analysis?.detected ?? true,
                severity: r.analysis?.severity || r.severity || Severity.LOW,
                mode: r.analysis?.mode || InspectionMode.GENERAL_NDT,
                timestamp: r.timestamp,
                frameImage: r.image,
                isVerified: true,
                findings: r.analysis?.findings || r.findings || []
              }];
            }) as any[]) as CrackAnalysis[];
            setLastTimeline(vaultTimeline);
          } else {
            setLastTimeline([]);
          }
        } catch (vaultErr) {
          setLastTimeline([]);
        }
      } else {
        setLastTimeline([]);
      }

      // STRICT RULE: local project → replace history view with verified records from folder
      if (isLocalProject && verifiedRecords.length > 0) {
        setHistory(verifiedRecords.map((r: any) => ({
          id: r.id || `local-${r.timestamp}`,
          projectId: projectId,
          timestamp: r.timestamp,
          imageUrl: r.image || "",
          analysis: r.analysis || r,
          timeline: r.timeline || [],
          videoName: r.videoName || (r.analysis?.type ? `${r.analysis.type} Event` : "Verified Finding")
        })));
      }

      // ── 3. CHECK FOR SESSION CHECKPOINT ─────────────────────────────────────
      const checkpoint = await getSessionCheckpoint();
      if (checkpoint) {
        if (confirm(`A pending session was found for project "${checkpoint.projectNumber || 'Active'}". Would you like to resume?`)) {
          setLastVideoFile(physicalAssets?.video || null);
          setLastVideoRange(checkpoint.range);

          if (physicalAssets?.telemetry && (!checkpoint.trajectory || checkpoint.trajectory.length === 0)) {
            const text = await physicalAssets.telemetry.text();
            const pts = parseTrajectoryCsv(text);
            setLastTrajectoryPoints(pts);
          } else {
            setLastTrajectoryPoints(checkpoint.trajectory);
          }

          setLastLasModelName(checkpoint.lasModelName);
          setLastModelPoints(checkpoint.modelPoints || []);
          setActiveModes(checkpoint.modes || [InspectionMode.GENERAL_NDT]);

          if (checkpoint.timeline && Array.isArray(checkpoint.timeline)) {
            setLastTimeline(prev => {
              const existingIds = new Set(prev.map(p => p.timestamp));
              const uniqueCheckpointEntries = checkpoint.timeline.filter((t: any) => !existingIds.has(t.timestamp));
              return [...prev, ...uniqueCheckpointEntries];
            });
          }

          if (checkpoint.view) setView(checkpoint.view);
          if (!actionToRun) return;
        }
      }

      // ── 4. BUILD MERGED FINDINGS (done before routing so available everywhere) ─
      const activeProject = projects.find(p => p.id === projectId) || projects[0];

      // Match raw findings to verified ones using type + rounded box as key.
      // NOT timestamp: raw uses video-time seconds, verified uses wall-clock ms.
      const makeVerifiedKey = (f: any): string => {
        const box = (f.box || [0, 0, 0, 0]).map((v: number) => Math.round(v)).join(',');
        return `${f.type || ''}|${box}`;
      };

      const verifiedKeySet = new Set<string>();
      verifiedRecords.forEach((r: any) => {
        (r.analysis?.findings || r.findings || []).forEach((f: any) => {
          verifiedKeySet.add(makeVerifiedKey(f));
        });
      });

      let allFindingsForLab: any[];
      if (rawFindings.length > 0) {
        allFindingsForLab = rawFindings.map((f: any) => ({
          ...f, isVerified: verifiedKeySet.has(makeVerifiedKey(f))
        }));
      } else if (verifiedFindings.length > 0) {
        allFindingsForLab = verifiedFindings;
      } else {
        allFindingsForLab = [];
      }

      const representativeImage = verifiedFindings[0]?.frameImage
        || rawFindings[0]?.frameImage
        || verifiedRecords[0]?.image
        || evidenceRecords[0]?.frameImage
        || evidenceRecords[0]?.image
        || "";

      console.log('[AuditLoad] verifiedKeySet size:', verifiedKeySet.size,
        '| raw:', rawFindings.length, '| allForLab:', allFindingsForLab.length,
        '| matched verified:', allFindingsForLab.filter((f: any) => f.isVerified).length);

      // ── 4. ONE-CLICK PHYSICAL ASSET SYNC ────────────────────────────────────
      if (physicalAssets && (physicalAssets.video || physicalAssets.telemetry || physicalAssets.pointCloud)) {
        if (physicalAssets.video) setLastVideoFile(physicalAssets.video);

        if (physicalAssets.telemetry) {
          const text = await physicalAssets.telemetry.text();
          const pts = parseTrajectoryCsv(text);
          setLastTrajectoryPoints(pts);
        }

        if (physicalAssets.pointCloud) {
          const ext = physicalAssets.pointCloud.name.toLowerCase().split('.').pop();
          let pts: TelemetryPoint[] = [];
          if (ext === 'las' || ext === 'laz') pts = await parseLasFile(physicalAssets.pointCloud);
          else pts = await parseTextPointCloud(physicalAssets.pointCloud);

          if (pts.length > 0) {
            setLastLasModelName(physicalAssets.pointCloud.name);
            const geom = calculateAssetGeometry(pts);
            const filtered = filterPointsToAssetBoundary(pts, geom, 0.5);
            setLastModelPoints(filtered);
            setLastGeometry(geom);
          }
        }

        // Pre-populate labTransferData with the full merged audit dataset so
        // the GEA Lab (reached via Technical Audit Report) shows correct counts.
        if (allFindingsForLab.length > 0) {
          setLabTransferData({ image: representativeImage, findings: [], timeline: [], allFindings: allFindingsForLab, customer: activeProject?.metadata?.customer, asset: activeProject?.metadata?.asset });
        }

        // Route to the appropriate VideoInspector
        if (physicalAssets.telemetry) {
          setSelectedToolId('Elios3 UAV Chambers');
          setLastInspectorView('VIDEO_INSPECTION_T');
          setView('VIDEO_INSPECTION_T');
        } else if (physicalAssets.pointCloud) {
          setSelectedToolId('Elios3 UAV Chambers');
          setLastInspectorView('VIDEO_INSPECTION_M');
          setView('VIDEO_INSPECTION_M');
        } else if (physicalAssets.video) {
          setSelectedToolId('Elios3 Video Only');
          setLastInspectorView('VIDEO_INSPECTION_S');
          setView('VIDEO_INSPECTION_S');
        }
        if (!actionToRun) return;
      }

      // ── 5. Pre-populate Lab data (but do NOT auto-navigate) ──
      // Data is ready when the user navigates to GEA Lab via Technical Audit Report.
      if (allFindingsForLab.length > 0) {
        setLabTransferData({ image: representativeImage, findings: [], timeline: [], allFindings: allFindingsForLab, customer: activeProject?.metadata?.customer, asset: activeProject?.metadata?.asset });
      }
    } finally {
      setIsLoading(false);
      // Run the intended action (e.g. going to Mission Planner)
      if (actionToRun) {
        actionToRun();
      }
    }
  };

  const onProjectCreated = (projectNumber: string, metadata?: ProjectMetadata, existingId?: string, keepHandle: boolean = false, auditMode: boolean = false) => {
    const id = saveProject(projectNumber, metadata, existingId);
    onProjectSelected(id, keepHandle, auditMode);
  };

  const handleTransferToLab = (image: string, findings: Finding[], timeline: CrackAnalysis[] = [], allFindings: Finding[] = []) => {
    const activeProject = projects.find(p => p.id === activeProjectId);

    // If labTransferData was pre-populated with the full audit dataset (all raw +
    // verified findings), merge it with the incoming findings so the Lab always
    // shows the correct total count — not just the verified subset from the timeline.
    const mergedAllFindings: Finding[] = (() => {
      const preloaded = labTransferData?.allFindings || [];
      if (preloaded.length > allFindings.length) {
        // Pre-loaded has more context (raw evidence) — use it as base.
        // Merge: mark findings from incoming allFindings as verified.
        const incomingKeys = new Set(allFindings.map((f: any) => {
          const box = (f.box || [0,0,0,0]).map((v: number) => Math.round(v)).join(',');
          return `${f.type || ''}|${box}`;
        }));
        return preloaded.map((f: any) => ({
          ...f,
          isVerified: f.isVerified || incomingKeys.has(
            `${f.type || ''}|${(f.box || [0,0,0,0]).map((v: number) => Math.round(v)).join(',')}`
          )
        }));
      }
      return allFindings.length > 0 ? allFindings : preloaded;
    })();

    setLabTransferData({
      image,
      findings,
      timeline,
      allFindings: mergedAllFindings,
      hasTelemetry: telemetryData.length > 0,
      customer: activeProject?.metadata?.customer,
      asset: activeProject?.metadata?.asset
    });
    setView('TRAINING');
  };


  const toggleMode = (mode: InspectionMode) => {
    setActiveModes(prev => {
      if (prev.includes(mode)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== mode);
      }
      return [...prev, mode];
    });
  };

  const processPhotos = (files: FileList | File[]) => {
    if (!activeProjectId) {
      handleActionWithProject(() => processPhotos(files));
      return;
    }
    const fileList = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (fileList.length === 0) return;

    setPendingImages(fileList);
    setView('IMAGE_INSPECTION');
  };

  const handleImageAnalysisComplete = (batchTimeline: CrackAnalysis[]) => {
    if (batchTimeline.length === 0) {
      alert("Analysis produced no results. Returning to Home.");
      setView('HOME');
      return;
    }

    let pid = activeProjectId;
    if (!pid) {
      console.warn("Project ID missing in completion handler. Recovering...");
      if (projects.length > 0) pid = projects[0].id;
      else pid = saveProject("Default_Rescue_Project");
      setActiveProjectId(pid);
    }

    const severityOrder = [Severity.NONE, Severity.LOW, Severity.MEDIUM, Severity.HIGH, Severity.CRITICAL];
    const worst = batchTimeline.reduce((prev, curr) =>
      severityOrder.indexOf(curr.severity) > severityOrder.indexOf(prev.severity) ? curr : prev,
      batchTimeline[0]
    );

    const record: InspectionRecord = {
      id: crypto.randomUUID(),
      projectId: pid!,
      timestamp: Date.now(),
      imageUrl: worst.frameImage || "",
      analysis: {
        ...worst,
        analysisDuration: batchTimeline.length * 2
      },
      timeline: batchTimeline,
      videoName: batchTimeline.length > 1 ? `Image Survey (${batchTimeline.length} files)` : (batchTimeline[0].videoName || 'Static Image')
    };

    saveToHistory(record);
    setCurrentImage(worst.frameImage || "");
    setCurrentAnalysis(record.analysis);
    setVideoTimeline(batchTimeline);
    setTelemetryData([]);
    setModelPoints([]);
    setLastLasModelName(null);
    setView('RESULTS');
  };

  const processTelemetryCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const pts = parseTrajectoryCsv(text);
      if (pts.length > 0) {
        setTelemetryData(pts);
        setView('TELEMETRY');
      } else {
        alert("Invalid Telemetry Data. Check format (Space-delimited columns).");
      }
    };
    reader.readAsText(file);
  };

  const handleVideoComplete = (videoUrl: string, timeline: CrackAnalysis[], videoName: string, evaluationDuration: number, fps: number, range: [number, number], metadata: any) => {
    if (!activeProjectId) return;

    if (metadata.file) {
      setLastVideoFile(metadata.file);
      setLastVideoRange(range);
    }
    if (metadata.trajectoryPoints) setLastTrajectoryPoints(metadata.trajectoryPoints);
    if (metadata.lasModelName) setLastLasModelName(metadata.lasModelName);
    if (metadata.modelPoints) setLastModelPoints(metadata.modelPoints);

    const severityOrder = [Severity.NONE, Severity.LOW, Severity.MEDIUM, Severity.HIGH, Severity.CRITICAL];
    const flightDuration = range[1] - range[0];
    const evaluationSpeed = evaluationDuration > 0 ? (flightDuration * 0.5) / evaluationDuration : 0;

    const worst = timeline.length > 0
      ? timeline.reduce((prev, curr) => severityOrder.indexOf(curr.severity) > severityOrder.indexOf(prev.severity) ? curr : prev, timeline[0])
      : {
        detected: false,
        severity: Severity.NONE,
        mode: activeModes[0],
        description: "No anomalies detected. Surface state nominal.",
        recommendation: "Continue routine cycles.",
        confidenceScore: 0.99,
        findings: [],
        frameImage: metadata.posterFrame,
        diagnosticNote: "Neural sweep complete. No morphology matches found."
      };

    const record: InspectionRecord = {
      id: crypto.randomUUID(),
      projectId: activeProjectId,
      timestamp: Date.now(),
      imageUrl: (worst.frameImage || metadata.posterFrame) || "",
      analysis: {
        ...worst,
        videoName,
        analysisDuration: evaluationDuration,
        flightDuration,
        evaluationSpeed
      } as CrackAnalysis,
      timeline: timeline.length > 0 ? timeline.map(t => ({
        ...t,
        videoName,
        analysisDuration: evaluationDuration,
        flightDuration,
        evaluationSpeed
      })) : [{ ...worst, timestamp: 0 } as any],
      videoName: videoName,
      videoFps: fps,
      analysisDuration: evaluationDuration,
      flightDuration,
      telemetry: metadata.trajectoryPoints || undefined,
      lasModelName: metadata.lasModelName || undefined,
      modelPoints: metadata.modelPoints || undefined
    };

    saveToHistory(record);
    setCurrentImage(record.imageUrl);
    setCurrentAnalysis(record.analysis);
    setVideoTimeline(record.timeline || null);
    setTelemetryData(record.telemetry || []);
    setModelPoints(record.modelPoints || []);
    setView('RESULTS');
  };


  const handleLiveComplete = (timeline: CrackAnalysis[], startTime: number, lastFrame?: string, trajectory?: TelemetryPoint[]) => {
    if (!activeProjectId) return;
    const sessionName = `Live Session ${new Date().toLocaleTimeString()}`;
    const evaluationDuration = (Date.now() - startTime) / 1000;

    const worst = timeline.length > 0
      ? timeline.reduce((prev, curr) => {
        const severityOrder = [Severity.NONE, Severity.LOW, Severity.MEDIUM, Severity.HIGH, Severity.CRITICAL];
        return severityOrder.indexOf(curr.severity) > severityOrder.indexOf(prev.severity) ? curr : prev;
      }, timeline[0])
      : {
        detected: false,
        severity: Severity.NONE,
        mode: activeModes[0],
        description: "Live monitoring complete. No surface anomalies logged.",
        recommendation: "Continue standard cycle.",
        confidenceScore: 0.95,
        findings: [],
        frameImage: lastFrame,
        diagnosticNote: "Live telemetry verified nominal."
      };

    const record: InspectionRecord = {
      id: crypto.randomUUID(),
      projectId: activeProjectId,
      timestamp: Date.now(),
      imageUrl: (worst.frameImage || lastFrame) || "",
      analysis: {
        ...worst,
        videoName: sessionName,
        analysisDuration: evaluationDuration,
        flightDuration: evaluationDuration,
        evaluationSpeed: 0.5
      } as CrackAnalysis,
      timeline: timeline.length > 0 ? timeline : [{ ...worst, timestamp: 0 } as any],
      videoName: sessionName,
      analysisDuration: evaluationDuration,
      telemetry: trajectory
    };

    saveToHistory(record);

    // PERSISTENCE POINT: Save each live detection to the project's Evidence folder (01_)
    if (timeline.length > 0) {
      timeline.forEach(async (analysis, i) => {
        if (analysis.frameImage) {
          const base64Data = analysis.frameImage.split(',')[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let k = 0; k < byteCharacters.length; k++) byteNumbers[k] = byteCharacters.charCodeAt(k);
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'image/jpeg' });
          const baseName = `live_finding_${Date.now()}_${i}`;
          await saveFileToProject('01_Evidence_Images', `${baseName}.jpg`, blob);

          const metaBlob = new Blob([JSON.stringify({
            ...analysis,
            id: `ev-${Date.now()}-${i}`,
            projectId: 'local',
            videoName: sessionName
          }, null, 2)], { type: 'application/json' });
          await saveFileToProject('01_Evidence_Images', `${baseName}.json`, metaBlob);
        }
      });
    }

    setCurrentImage(record.imageUrl);
    setCurrentAnalysis(record.analysis);
    setVideoTimeline(record.timeline || null);
    setTelemetryData(record.telemetry || []);
    setModelPoints([]);
    setView('RESULTS');
  };


  const handleRerunRequested = () => {
    if (lastVideoFile) {
      if (selectedToolId === 'Elios3 Video Only') setView('VIDEO_INSPECTION_S');
      else setView('VIDEO_INSPECTION_T');
    } else {
      alert("Re-run available for Video Surveys. For images, please re-upload.");
      setView('HOME');
    }
  };

  const handleMissionTransfer = (video: File | null, trajectory: TelemetryPoint[], points: TelemetryPoint[], overlap?: number, lasModelName?: string | null, geometry?: any) => {
    setLastVideoFile(video);
    setLastTrajectoryPoints(trajectory);
    setLastModelPoints(points);
    setLastLasModelName(lasModelName || null);
    setLastVideoRange(null);
    setLastOverlap(overlap);
    setLastGeometry(geometry);
    setSelectedToolId('Elios3 UAV Chambers');
    setLastInspectorView('VIDEO_INSPECTION_M');
    handleActionWithProject(() => setView('VIDEO_INSPECTION_M'));
  };

  const startFreshVideoSurvey = (targetView: ViewState) => {
    setLastVideoFile(null);
    setLastVideoRange(null);
    setLastTrajectoryPoints(null);
    setLastLasModelName(null);
    setLastModelPoints([]);
    setLastOverlap(undefined);
    setLastGeometry(null);
    setLastTimeline([]);
    setLastInspectorView(targetView);

    // Clear operational state to ensure a truly fresh start
    setVideoTimeline(null);
    setTelemetryData([]);
    setModelPoints([]);
    setCurrentAnalysis(null);
    setCurrentImage(null);
    setLabUpdatedFindings(null);
    setLabTransferData(null);
    setActiveModes([InspectionMode.GENERAL_NDT]);

    setLastInspectorView(targetView);
    setView(targetView);
  };

  const deleteProject = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete project and its records?")) return;
    setProjects(prev => prev.filter(p => p.id !== id));
    setHistory(prev => prev.filter(r => r.projectId !== id));
  };

  const deleteInspection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this record?")) return;
    setHistory(prev => {
      const updated = prev.filter(r => r.id !== id);
      try {
        const storageHistory = updated.slice(0, 20).map(record => ({
          ...record,
          imageUrl: '',
          analysis: record.analysis ? {
            ...record.analysis,
            frameImage: undefined,
            findings: record.analysis.findings?.map((f: any) => ({ ...f, frameImage: undefined }))
          } : record.analysis,
          timeline: record.timeline?.map(t => ({
            ...t,
            frameImage: undefined
          }))
        }));
        localStorage.setItem('staincheck_history', JSON.stringify(storageHistory));
      } catch (e) {
        console.warn("Could not sync deletion to storage");
      }
      return updated;
    });
  };


  // --- Start Buttons Handlers ---
  const handleImageSurvey = () => {
    setSelectedToolId('none');
    setPendingImages([]); // Start with fresh empty list to transition to UI first
    setLabUpdatedFindings(null);
    setLastInspectorView('IMAGE_INSPECTION');
    handleActionWithProject(() => setView('IMAGE_INSPECTION'));
  };

  const handleVideoSurvey = () => {
    setSelectedToolId('Elios3 Video Only');
    setLastInspectorView('VIDEO_INSPECTION_S');
    handleActionWithProject(() => startFreshVideoSurvey('VIDEO_INSPECTION_S'), true);
  };

  const handleE3TelemetrySurvey = () => {
    setSelectedToolId('Elios3 UAV Chambers');
    setLastInspectorView('VIDEO_INSPECTION_T');
    handleActionWithProject(() => startFreshVideoSurvey('VIDEO_INSPECTION_T'), true);
  };

  const handleLiveScan = () => {
    setSelectedToolId('custom');
    setLastInspectorView('LIVE_SCAN');
    handleActionWithProject(() => setView('LIVE_SCAN'));
  };

  const handleE3Autonomy = () => {
    setSelectedToolId('Elios3 UAV Chambers');
    handleActionWithProject(() => setView('MISSION_PLANNER'), false);
  };

  const handleLoadAuditFolder = async () => {
    const result = await loadProjectFromLocal();
    if (result) {
      const { manifest } = result;
      // auditMode=true → skip VideoInspector routing, go straight to Lab with findings
      onProjectCreated(manifest.projectNumber, manifest.metadata, manifest.id, true, true);
    }
  };

  const handleLogout = () => {
    logSessionEnd();
    sessionStorage.clear();
    window.location.replace(window.location.origin + window.location.pathname);
  };

  const renderHome = () => (

    <div className="flex-1 overflow-y-auto custom-scrollbar h-full bg-slate-400">
      <div className="max-w-[90%] mx-auto p-6 space-y-12 animate-fadeIn pb-24 text-left">
        <header className="flex items-center justify-between gap-6 bg-slate-300 p-10 border border-slate-500 shadow-sm relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0000a9]/5 to-transparent opacity-50 pointer-events-none"></div>
          <div className="flex items-center gap-10 relative z-10">
            <div className="w-24 h-24 bg-[#0000a9] flex items-center justify-center shadow-[0_20px_40px_rgba(0,0,169,0.2)] relative group-hover:scale-105 transition-transform duration-500">
              <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-slate-300/30"></div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-5xl font-black tracking-tighter text-slate-900 uppercase italic leading-none">
                GEA <span className="text-slate-300 font-light mx-2">|</span> <span className="not-italic text-[#0000a9]">NDT SCANNER</span>
              </h1>
              <span className="text-[12px] font-black text-[#0000a9] tracking-[0.6em] mt-4 uppercase italic">
                BUILD V6.0 PRO <span className="mx-2 text-slate-200">/</span> NEURAL INSPECTION INTERFACE
              </span>
            </div>
          </div>

          <div className="flex items-center gap-6 relative z-10 h-24">

            <button
              onClick={handleLogout}
              className="flex items-center gap-8 bg-slate-300 p-5 px-8 border border-slate-500 hover:border-red-500 transition-all group cursor-pointer shadow-sm relative z-10 h-full border-l-0"
            >
              <div className="flex flex-col gap-1 text-left">
                <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest leading-none group-hover:text-red-500 transition-colors">Terminate Link</span>
                <span className="text-[10px] font-black text-slate-900 uppercase italic mt-1 font-black">SIGN_OUT</span>
              </div>
              <div className="w-14 h-14 bg-red-500/80 flex items-center justify-center border border-slate-500 group-hover:bg-red-500 text-white transition-all shadow-sm shrink-0">
                <svg className="w-8 h-8 font-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
            </button>
          </div>
        </header>

        <section className="space-y-8">
          <div className="flex items-center justify-between border-b border-slate-500 pb-6 max-w-6xl mx-auto">
            <h2 className="text-[12px] font-black text-slate-900 uppercase tracking-[0.6em] italic">Inspection Mode Registry</h2>
            <div className="flex items-center gap-4">
              <button onClick={() => generateRegulationsPdf()} className="flex items-center gap-3 px-6 py-4 bg-slate-300 hover:bg-slate-400 text-slate-700 hover:text-[#0000a9] border border-slate-400 transition-all text-[9px] font-black uppercase tracking-widest shadow-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                AI NEURAL & VISUAL STANDARDS.PDF
              </button>
              <button onClick={generateUserManual} className="flex items-center gap-3 px-6 py-4 bg-slate-300 hover:bg-slate-400 text-slate-700 hover:text-[#0000a9] border border-slate-400 transition-all text-[9px] font-black uppercase tracking-widest shadow-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                USER MANUAL.PDF
              </button>
              <button onClick={generateGDPRManual} className="flex items-center gap-3 px-6 py-4 bg-slate-300 hover:bg-slate-400 text-slate-700 hover:text-[#0000a9] border border-slate-400 transition-all text-[9px] font-black uppercase tracking-widest shadow-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                GDPR_DATA_FLOW.PDF
              </button>
              <div className="flex items-center gap-3 px-6 py-4 bg-[#0000a9] text-white border border-[#0000a9] shadow-xl text-[9px] font-black uppercase tracking-widest italic">
                <div className="w-2 h-2 bg-slate-300 animate-pulse"></div>
                MULTI-MODE CAPABLE
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full max-w-6xl mx-auto">
            {PROTOCOL_OPTIONS.map(protocol => {
              const isActive = activeModes.includes(protocol.id);
              return (
                <button
                  key={protocol.id}
                  onClick={() => toggleMode(protocol.id)}
                  className={`relative group aspect-video p-10 border text-left transition-all overflow-hidden flex flex-col justify-between shadow-xl rounded-none ${isActive ? 'bg-[#0000a9] border-[#ffffff]/20 shadow-[0_20px_50px_rgba(0,0,169,0.3)]' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]/50 hover:bg-slate-300'}`}
                >
                  <div className={`transition-all duration-300 ${isActive ? 'text-white' : 'text-slate-800 group-hover:text-[#0000a9]/60'}`}>
                    {protocol.icon}
                  </div>
                  <div className="flex justify-between items-end w-full relative z-10">
                    <div>
                      <h3 className={`font-black text-[11px] uppercase tracking-widest leading-none mb-3 ${isActive ? 'text-white' : 'text-slate-900 group-hover:text-[#0000a9]'}`}>
                        {protocol.label}
                      </h3>
                      <p className={`text-[9px] font-black uppercase tracking-wider leading-none ${isActive ? 'text-white/60' : 'text-slate-800'}`}>
                        {protocol.desc}
                      </p>
                    </div>
                    {isActive && (
                      <div className="w-2 h-2 bg-slate-300 animate-pulse"></div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="space-y-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[12px] font-black text-slate-900 uppercase tracking-[0.6em] border-b border-slate-500 pb-6 italic">Engineering Data Services</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingImages(true); }}
              onDragLeave={() => setIsDraggingImages(false)}
              onDrop={(e) => { e.preventDefault(); setIsDraggingImages(false); if (e.dataTransfer.files) processPhotos(e.dataTransfer.files); }}
              className={`group relative aspect-square flex flex-col justify-between bg-slate-300 p-10 text-left transition-all shadow-sm overflow-hidden active:scale-[0.98] cursor-pointer border-2 ${isDraggingImages ? 'bg-slate-400 border-[#0000a9]' : 'border-slate-400 hover:border-[#0000a9]/30 hover:shadow-xl'}`}
              onClick={handleImageSurvey}
            >
              <input type="file" autoFocus={false} ref={photoInputRef} className="hidden" accept="image/*" multiple onChange={(e) => e.target.files && processPhotos(e.target.files)} />
              <div className={`w-20 h-20 flex items-center justify-center transition-all ${isDraggingImages ? 'text-[#0000a9]' : 'text-slate-800 group-hover:text-[#0000a9]'}`}>
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" /></svg>
              </div>
              <div className="flex justify-between items-end w-full">
                <h3 className="text-slate-900 font-black text-xl uppercase tracking-tighter leading-none italic group-hover:text-[#0000a9]">Image<br />Survey</h3>
                <svg className="w-6 h-6 text-slate-800 group-hover:text-[#0000a9] transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </div>

            <button onClick={handleVideoSurvey} className="group relative aspect-square flex flex-col justify-between bg-slate-300 p-10 text-left transition-all shadow-sm overflow-hidden active:scale-[0.98] border-2 border-slate-400 hover:border-[#0000a9]/30 hover:shadow-xl">
              <div className="w-20 h-20 flex items-center justify-center text-slate-800 group-hover:text-[#0000a9] transition-all">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg>
              </div>
              <div className="flex justify-between items-end w-full">
                <h3 className="text-slate-900 font-black text-xl uppercase tracking-tighter leading-none italic group-hover:text-[#0000a9]">Video<br />Survey</h3>
                <svg className="w-6 h-6 text-slate-800 group-hover:text-[#0000a9] transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </button>

            <button onClick={handleE3TelemetrySurvey} className="group relative aspect-square flex flex-col justify-between bg-slate-300 p-10 text-left transition-all shadow-sm overflow-hidden active:scale-[0.98] border-2 border-slate-400 hover:border-[#0000a9]/30 hover:shadow-xl">
              <div className="w-20 h-20 flex items-center justify-center text-slate-800 group-hover:text-[#0000a9] transition-all">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 7m0 10V7m0 0L9 4" /></svg>
              </div>
              <div className="flex justify-between items-end w-full">
                <h3 className="text-slate-900 font-black text-xl uppercase tracking-tighter leading-none italic group-hover:text-[#0000a9]">Elios 3<br />Spatial</h3>
                <svg className="w-6 h-6 text-slate-800 group-hover:text-[#0000a9] transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </button>

            <button onClick={handleE3Autonomy} className="group relative aspect-square flex flex-col justify-between bg-slate-300 p-10 text-left transition-all shadow-sm overflow-hidden active:scale-[0.98] border-2 border-slate-400 hover:border-[#0000a9]/30 hover:shadow-xl">
              <div className="w-20 h-20 flex items-center justify-center text-slate-800 group-hover:text-[#0000a9] transition-all">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
              </div>
              <div className="flex justify-between items-end w-full">
                <h3 className="text-slate-900 font-black text-xl uppercase tracking-tighter leading-none italic group-hover:text-[#0000a9]">Autonomy<br />Planner</h3>
                <svg className="w-6 h-6 text-slate-800 group-hover:text-[#0000a9] transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </button>

            <button onClick={handleLiveScan} className="group relative aspect-square flex flex-col justify-between bg-slate-300 p-10 text-left transition-all shadow-sm overflow-hidden active:scale-[0.98] border-2 border-slate-400 hover:border-[#0000a9]/30 hover:shadow-xl">
              <div className="w-20 h-20 flex items-center justify-center text-slate-800 group-hover:text-[#0000a9] transition-all">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2-2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>
              </div>
              <div className="flex justify-between items-end w-full">
                <h3 className="text-slate-900 font-black text-xl uppercase tracking-tighter leading-none italic group-hover:text-[#0000a9]">Live<br />Scanner</h3>
                <svg className="w-6 h-6 text-slate-800 group-hover:text-[#0000a9] transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </button>
          </div>
        </section>

        {/* RECENT ACTIVITY SECTION */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6 border-t border-slate-500 max-w-6xl mx-auto">
          {/* Projects List */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-[12px] font-black text-slate-900 uppercase tracking-[0.6em]">ACTIVE PROJECT ENVIRONMENT</h2>
              <span className="text-[9px] font-bold bg-[#0000a9] text-white px-2 py-1 border border-[#0000a9] uppercase">{projects.length} Folders</span>
            </div>
            <div className="grid grid-cols-1 gap-3 max-h-[320px] overflow-y-auto custom-scrollbar pr-2">
              {projects.map(p => (
                <div
                  key={p.id}
                  onClick={() => onProjectSelected(p.id)}
                  className={`group flex items-center justify-between p-6 border ${activeProjectId === p.id ? 'border-[#0000a9] bg-slate-400 shadow-xl' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/30 hover:bg-slate-200'} cursor-pointer transition-all relative overflow-hidden`}
                >
                  {activeProjectId === p.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000a9]"></div>}
                  <div className="flex items-center gap-6">
                    <div className={`w-14 h-14 flex items-center justify-center transition-all duration-500 ${activeProjectId === p.id ? 'bg-[#0000a9] text-white' : 'bg-slate-400 text-slate-800 group-hover:bg-[#0000a9] group-hover:text-white border border-slate-500'}`}>
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className={`font-black text-sm uppercase tracking-wide ${activeProjectId === p.id ? 'text-[#0000a9]' : 'text-slate-900'}`}>{p.projectNumber}</h4>
                        {activeProjectId === p.id && <span className="text-[9px] font-bold bg-[#0000a9] text-white px-3 py-1 border border-white/20">ACTIVE</span>}
                      </div>
                      <p className="text-[10px] text-slate-800 font-mono mt-1 uppercase tracking-wider">
                        {(p.metadata?.customer || 'Unknown Client')} <span className="text-slate-300 mx-1">/</span> {(p.metadata?.asset || 'General Asset')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button onClick={(e) => deleteProject(p.id, e)} className="p-3 text-slate-800 hover:text-red-500 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Files List */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-[12px] font-black text-slate-900 uppercase tracking-[0.6em]">INSPECTION HISTORY</h2>
              <span className="text-[9px] font-bold bg-[#0000a9] text-white px-2 py-1 border border-[#0000a9]">{history.length} RECORDS</span>
            </div>
            <div className="grid grid-cols-1 gap-3 max-h-[320px] overflow-y-auto custom-scrollbar pr-2">
              {history.map(record => (
                <div key={record.id} onClick={() => {
                  setCurrentImage(record.imageUrl);
                  setCurrentAnalysis(record.analysis);
                  setVideoTimeline(record.timeline || null);
                  setTelemetryData(record.telemetry || []);
                  setModelPoints(record.modelPoints || []);
                  setLastLasModelName(record.lasModelName || null);
                  setView('RESULTS');
                }} className="group flex items-center gap-6 p-5 bg-slate-300 border border-slate-400 hover:border-[#0000a9]/30 cursor-pointer transition-all shadow-sm">
                  <div className="w-24 h-16 bg-slate-900 overflow-hidden border border-slate-500 relative shrink-0 shadow-sm">
                    <img src={record.imageUrl} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    <div className={`absolute top-2 right-2 w-3 h-3 ${record.analysis.detected ? 'bg-red-500 shadow-[0_0_10px_red]' : 'bg-emerald-500 shadow-[0_0_10px_#10b981]'} border border-white/50`}></div>
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-slate-900 font-black text-xs uppercase tracking-wider truncate pr-4">{record.videoName || 'Unnamed Record'}</h4>
                      <span className="text-[10px] font-mono text-slate-800">{new Date(record.timestamp).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-[10px] font-black uppercase px-3 py-1 ${record.analysis.detected ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}`}>
                        {record.analysis.severity}
                      </span>
                      <span className="text-[10px] text-slate-800 font-mono truncate uppercase tracking-widest">{record.id.split('-')[0]}</span>
                    </div>
                  </div>
                  <button onClick={(e) => deleteInspection(record.id, e)} className="p-3 text-slate-800 hover:text-red-500 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100 shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );

  return (
    <LoginGuard>
      <div className="h-screen w-full flex flex-col bg-slate-400 font-sans selection:bg-[#0000a9]/10 selection:text-[#0000a9]">
        {view === 'HOME' && renderHome()}

        {view === 'IMAGE_INSPECTION' && (
          <ImageInspector
            onCancel={() => setView('HOME')}
            onComplete={handleImageAnalysisComplete}
            initialFiles={pendingImages}
          />
        )}

        {view === 'VIDEO_INSPECTION_S' && (
          <VideoInspectorS
            onCancel={() => setView('HOME')}
            onComplete={handleVideoComplete}
            initialFile={lastVideoFile || undefined}
            initialRange={lastVideoRange || undefined}
            initialModes={activeModes}
            initialTimeline={lastTimeline}
            toolFps={INSPECT_TOOLS.find(t => t.id === selectedToolId)?.defaultFps}
            toolSpecs={INSPECT_TOOLS.find(t => t.id === selectedToolId)?.specs}
            toolId={selectedToolId}
          />
        )}

        {view === 'VIDEO_INSPECTION_T' && (
          <VideoInspectorT
            onCancel={() => setView('HOME')}
            onComplete={handleVideoComplete}
            initialFile={lastVideoFile || undefined}
            initialRange={lastVideoRange || undefined}
            initialModes={activeModes}
            initialTrajectoryPoints={lastTrajectoryPoints || []}
            initialLasModelName={lastLasModelName || undefined}
            initialModelPoints={lastModelPoints || []}
            initialTimeline={lastTimeline}
            toolFps={INSPECT_TOOLS.find(t => t.id === selectedToolId)?.defaultFps}
            toolSpecs={INSPECT_TOOLS.find(t => t.id === selectedToolId)?.specs}
            toolId={selectedToolId}
            initialCloudOffset={persistentCloudOffset}
          />
        )}

        {view === 'VIDEO_INSPECTION_M' && (
          <VideoInspectorM
            onCancel={() => setView('HOME')}
            onComplete={handleVideoComplete}
            initialFile={lastVideoFile || undefined}
            initialRange={lastVideoRange || undefined}
            initialModes={activeModes}
            initialTrajectoryPoints={lastTrajectoryPoints || undefined}
            initialLasModelName={lastLasModelName || undefined}
            initialModelPoints={lastModelPoints}
            initialOverlap={lastOverlap}
            initialGeometry={lastGeometry}
            initialTimeline={lastTimeline}
            toolId={selectedToolId}
            initialCloudOffset={persistentCloudOffset}
          />
        )}

        {view === 'RESULTS' && currentAnalysis && (() => {
          const activeProject = projects.find(p => p.id === activeProjectId);
          return (
            <AnalysisResults
              image={currentImage || ""}
              analysis={currentAnalysis}
              timeline={videoTimeline || []}
              telemetryPoints={telemetryData}
              onClose={() => setView(lastInspectorView || 'HOME')}
              onRerun={handleRerunRequested}
              onTransferToLab={handleTransferToLab}
              setView={setView}
              customer={activeProject?.metadata?.customer}
              asset={activeProject?.metadata?.asset}
            />
          );
        })()}

        {view === 'LIVE_SCAN' && (
          <LiveScanner
            onCancel={() => setView('HOME')}
            onComplete={handleLiveComplete}
            initialModes={activeModes}
            toolSpecs={selectedTool.specs}
            toolFps={selectedTool.defaultFps}
            toolId={selectedToolId}
          />
        )}

        {view === 'TRAINING' && (() => {
          // Derive a stable key from the lab data so React remounts TrainingModule
          // whenever new inspection data arrives — prevents stale useState from showing old frames.
          const labKey = labTransferData
            ? `lab-${labTransferData.allFindings?.length ?? 0}-${labTransferData.image?.slice(-20) ?? 'empty'}`
            : 'lab-empty';
          return (
            <TrainingModule
              key={labKey}
              onClose={() => setView('RESULTS')}
              onViewTrajectory={(updated) => {
                setLabUpdatedFindings(updated);
                setLabTransferData(prev => prev ? { ...prev, allFindings: updated } : null);
                setView('TELEMETRY');
              }}
              initialLabData={labTransferData || undefined}
            />
          );
        })()}

        {view === 'TELEMETRY' && (
          <div className="h-full w-full bg-slate-400 relative">
            <TrajectoryMapA
              points={telemetryData}
              modelPoints={modelPoints}
              findings={labUpdatedFindings || consolidatedFindings}
              initialCloudOffset={persistentCloudOffset}
              onAlignChange={setPersistentCloudOffset}
              onClose={() => setView(labUpdatedFindings ? 'TRAINING' : 'RESULTS')}
              previewMode="BOTH"
              mapMode="ANALYSIS"
            />
          </div>
        )}

        {view === 'MISSION_PLANNER' && (
          <MissionPlanner
            onCancel={() => setView('HOME')}
            onTransfer={handleMissionTransfer}
            initialCloudOffset={persistentCloudOffset}
          />
        )}


        {showProjectModal && (
          <ProjectModal
            projects={projects}
            onSelect={onProjectSelected}
            onCreate={onProjectCreated}
            onCancel={() => setShowProjectModal(false)}
          />
        )}

        {showQrModal && (
          <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-8 animate-fadeIn" onClick={() => setShowQrModal(false)}>
            <div className="bg-slate-300 p-10 shadow-[0_40px_100px_rgba(0,0,169,0.2)] max-w-sm w-full text-center border border-slate-400" onClick={e => e.stopPropagation()}>
              <h3 className="text-slate-900 font-black uppercase text-xl mb-6 tracking-widest italic">Companion Sync</h3>
              <div className="bg-white p-4 border border-slate-400 mb-8 flex items-center justify-center">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.href)}`} alt="QR Code" className="w-full h-auto" />
              </div>
              <p className="text-slate-800 text-[10px] font-black uppercase tracking-widest mb-8 leading-relaxed">Scan to access Live Scanner<br />on your mobile device.</p>
              <button onClick={() => setShowQrModal(false)} className="w-full py-5 bg-[#0000a9] text-white font-black uppercase text-[10px] tracking-widest hover:bg-[#0000c0] transition-all shadow-lg active:scale-95">Dismiss Interface</button>
            </div>
          </div>
        )}
        {isLoading && <LoadingOverlay message="SYNCHRONIZING PROJECT" subMessage="RECOVERING INSPECTION ARTIFACTS FROM LOCAL REPOSITORY" />}
      </div>
    </LoginGuard>
  );
};

export default App;